import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { TrendingUp, TrendingDown, MousePointerClick, Eye, Link as LinkIcon, FileText, AlertCircle, CheckCircle2, Users, Clock, Activity } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';

interface GscSnapshot {
  id: string;
  snapshot_date: string;
  total_clicks: number | null;
  total_impressions: number | null;
  average_ctr: number | null;
  average_position: number | null;
  indexed_pages: number | null;
  not_indexed_pages: number | null;
  discovered_pages: number | null;
  external_links: number | null;
  top_queries: Array<{ query: string; clicks: number; impressions: number }> | null;
  indexing_issues: Array<{ reason: string; pages: number }> | null;
  sitemap_url_count: number | null;
  db_indexable_count: number | null;
  blog_published: number | null;
  blog_pending: number | null;
  social_published: number | null;
  scenarios_with_ai: number | null;
  scenarios_total: number | null;
  seo_health_score: number | null;
  daily_summary: string | null;
  daily_plan: string | null;
  proposed_tasks: Array<{ priority: string; title: string }> | null;
  news_topics: Array<{ topic: string }> | null;
  // GA4 fields
  ga4_active_users: number | null;
  ga4_total_users: number | null;
  ga4_sessions: number | null;
  ga4_engaged_sessions: number | null;
  ga4_engagement_rate: number | null;
  ga4_avg_session_duration: number | null;
  ga4_screen_page_views: number | null;
  ga4_event_count: number | null;
  ga4_traffic_sources: Array<{ channel: string; sessions: number; engaged_sessions: number }> | null;
  ga4_top_pages: Array<{ page: string; views: number; users: number; avg_duration: number }> | null;
  ga4_top_events: Array<{ event: string; count: number }> | null;
  ga4_top_countries: Array<{ country: string; users: number; sessions: number }> | null;
}

const priorityColors: Record<string, string> = {
  critical: 'destructive',
  high: 'default',
  medium: 'secondary',
  low: 'outline',
};

const SEODailyTracker: React.FC = () => {
  const { data: snapshots, isLoading, error } = useQuery({
    queryKey: ['gsc-daily-snapshots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gsc_daily_snapshots' as any)
        .select('*')
        .order('snapshot_date', { ascending: true })
        .limit(90);
      if (error) throw error;
      return (data || []) as unknown as GscSnapshot[];
    },
  });

  if (isLoading) return <div className="p-6">Loading SEO data...</div>;
  if (error) return <div className="p-6 text-red-600">Error loading SEO data: {(error as Error).message}</div>;
  if (!snapshots || snapshots.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>No SEO snapshots yet</CardTitle>
            <CardDescription>Run the daily SEO check-in to generate the first snapshot.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const latest = snapshots[snapshots.length - 1];
  const previous = snapshots.length > 1 ? snapshots[snapshots.length - 2] : null;

  const delta = (current: number | null, prev: number | null) => {
    if (current == null || prev == null || prev === 0) return null;
    return ((current - prev) / prev) * 100;
  };

  const clicksDelta = delta(latest.total_clicks, previous?.total_clicks ?? null);
  const impressionsDelta = delta(latest.total_impressions, previous?.total_impressions ?? null);
  const indexedDelta = delta(latest.indexed_pages, previous?.indexed_pages ?? null);

  // Format chart data
  const chartData = snapshots.map(s => ({
    date: new Date(s.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    clicks: s.total_clicks || 0,
    impressions: s.total_impressions || 0,
    ctr: s.average_ctr || 0,
    position: s.average_position || 0,
    indexed: s.indexed_pages || 0,
    notIndexed: s.not_indexed_pages || 0,
    discovered: s.discovered_pages || 0,
    backlinks: s.external_links || 0,
    healthScore: s.seo_health_score || 0,
    activeUsers: s.ga4_active_users || 0,
    sessions: s.ga4_sessions || 0,
    pageViews: s.ga4_screen_page_views || 0,
    engagementRate: s.ga4_engagement_rate ? s.ga4_engagement_rate * 100 : 0,
  }));

  const hasGa4Data = (latest.ga4_sessions ?? 0) > 0;

  const indexingPercent = latest.sitemap_url_count
    ? ((latest.indexed_pages || 0) / latest.sitemap_url_count * 100).toFixed(1)
    : '—';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">SEO Daily Tracker</h2>
        <p className="text-muted-foreground">
          Last update: {new Date(latest.snapshot_date).toLocaleDateString()} • {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''} on file
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clicks</CardTitle>
            <MousePointerClick className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latest.total_clicks?.toLocaleString() ?? '—'}</div>
            {clicksDelta !== null && (
              <p className={`text-xs ${clicksDelta >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                {clicksDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {clicksDelta >= 0 ? '+' : ''}{clicksDelta.toFixed(1)}% vs previous
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Impressions</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latest.total_impressions?.toLocaleString() ?? '—'}</div>
            {impressionsDelta !== null && (
              <p className={`text-xs ${impressionsDelta >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                {impressionsDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {impressionsDelta >= 0 ? '+' : ''}{impressionsDelta.toFixed(1)}% vs previous
              </p>
            )}
            <p className="text-xs text-muted-foreground">CTR: {latest.average_ctr?.toFixed(2) ?? '—'}% • Pos: {latest.average_position?.toFixed(1) ?? '—'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Indexed Pages</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latest.indexed_pages?.toLocaleString() ?? '—'}</div>
            {indexedDelta !== null && (
              <p className={`text-xs ${indexedDelta >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                {indexedDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {indexedDelta >= 0 ? '+' : ''}{indexedDelta.toFixed(1)}% vs previous
              </p>
            )}
            <p className="text-xs text-muted-foreground">{indexingPercent}% of {latest.sitemap_url_count?.toLocaleString() ?? '—'} sitemap URLs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">External Backlinks</CardTitle>
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latest.external_links?.toLocaleString() ?? '—'}</div>
            <p className="text-xs text-muted-foreground">Critical bottleneck for ranking</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Summary & Plan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Today's Summary
            </CardTitle>
            <CardDescription>{new Date(latest.snapshot_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{latest.daily_summary || 'No summary recorded.'}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              The Plan
            </CardTitle>
            <CardDescription>Priorities and next actions</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{latest.daily_plan || 'No plan recorded.'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Trend Charts */}
      <Tabs defaultValue="performance" className="w-full">
        <TabsList>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="indexing">Indexing</TabsTrigger>
          <TabsTrigger value="ga4">User Behavior</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="queries">Top Queries</TabsTrigger>
          <TabsTrigger value="issues">Indexing Issues</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle>Search Performance Over Time</CardTitle>
              <CardDescription>Clicks and impressions from Google Search Console</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#3b82f6" strokeWidth={2} name="Clicks" />
                  <Line yAxisId="right" type="monotone" dataKey="impressions" stroke="#8b5cf6" strokeWidth={2} name="Impressions" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Average CTR</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis unit="%" />
                    <Tooltip />
                    <Area type="monotone" dataKey="ctr" stroke="#10b981" fill="#10b98133" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Average Position</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis reversed domain={[1, 'auto']} />
                    <Tooltip />
                    <Area type="monotone" dataKey="position" stroke="#f59e0b" fill="#f59e0b33" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="indexing">
          <Card>
            <CardHeader>
              <CardTitle>Indexing Coverage Over Time</CardTitle>
              <CardDescription>How many pages Google has indexed vs discovered</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="indexed" stackId="1" stroke="#10b981" fill="#10b98166" name="Indexed" />
                  <Area type="monotone" dataKey="notIndexed" stackId="1" stroke="#ef4444" fill="#ef444466" name="Not Indexed" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>External Backlinks</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="backlinks" stroke="#ec4899" strokeWidth={2} name="Backlinks" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ga4">
          {!hasGa4Data ? (
            <Card>
              <CardHeader>
                <CardTitle>Google Analytics 4 — Not yet collecting</CardTitle>
                <CardDescription>
                  GA4 Data API needs to be enabled in the hallofshame GCP project,
                  and the service account <code className="text-xs bg-muted px-1 rounded">id-gsc-reader@hallofshame.iam.gserviceaccount.com</code> must
                  be added as a Viewer in GA4 property 525787981. Once both are done, the next
                  daily cron run (07:13 server time) will begin populating user behavior data here.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Users (28d)</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{latest.ga4_active_users?.toLocaleString() ?? '—'}</div>
                    <p className="text-xs text-muted-foreground">{latest.ga4_total_users?.toLocaleString() ?? '—'} total users</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Sessions</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{latest.ga4_sessions?.toLocaleString() ?? '—'}</div>
                    <p className="text-xs text-muted-foreground">{latest.ga4_engaged_sessions?.toLocaleString() ?? '—'} engaged</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Engagement Rate</CardTitle>
                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {latest.ga4_engagement_rate != null ? `${(latest.ga4_engagement_rate * 100).toFixed(1)}%` : '—'}
                    </div>
                    <p className="text-xs text-muted-foreground">Of all sessions</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Session</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {latest.ga4_avg_session_duration != null
                        ? `${Math.floor(latest.ga4_avg_session_duration / 60)}m ${Math.round(latest.ga4_avg_session_duration % 60)}s`
                        : '—'}
                    </div>
                    <p className="text-xs text-muted-foreground">{latest.ga4_event_count?.toLocaleString() ?? '—'} events total</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="mb-4">
                <CardHeader>
                  <CardTitle>Users & Sessions Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="activeUsers" stroke="#06b6d4" strokeWidth={2} name="Active Users" />
                      <Line type="monotone" dataKey="sessions" stroke="#8b5cf6" strokeWidth={2} name="Sessions" />
                      <Line type="monotone" dataKey="pageViews" stroke="#10b981" strokeWidth={2} name="Page Views" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Traffic Sources</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(latest.ga4_traffic_sources || []).slice(0, 8).map((s, i) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span>{s.channel}</span>
                          <span className="text-muted-foreground">{s.sessions.toLocaleString()} sessions</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Top Countries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(latest.ga4_top_countries || []).slice(0, 8).map((c, i) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span>{c.country}</span>
                          <span className="text-muted-foreground">{c.users.toLocaleString()} users</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-sm">Top Pages by Views</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {(latest.ga4_top_pages || []).slice(0, 15).map((p, i) => (
                      <div key={i} className="flex justify-between items-center text-sm py-1 border-b last:border-b-0">
                        <span className="truncate flex-1 mr-4 font-mono text-xs">{p.page}</span>
                        <div className="flex gap-3 text-muted-foreground text-xs">
                          <span><strong>{p.views.toLocaleString()}</strong> views</span>
                          <span>{p.users.toLocaleString()} users</span>
                          <span>{p.avg_duration}s avg</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-sm">Top Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {(latest.ga4_top_events || []).map((e, i) => (
                      <div key={i} className="flex justify-between items-center text-sm py-1 border-b last:border-b-0">
                        <span className="font-mono text-xs">{e.event}</span>
                        <span className="text-muted-foreground">{e.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="content">
          <Card>
            <CardHeader>
              <CardTitle>Content Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 border rounded">
                  <div className="text-3xl font-bold text-green-600">{latest.blog_published ?? 0}</div>
                  <div className="text-sm text-muted-foreground">Blog Published</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <div className="text-3xl font-bold text-orange-600">{latest.blog_pending ?? 0}</div>
                  <div className="text-sm text-muted-foreground">Blog Pending</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <div className="text-3xl font-bold text-blue-600">{latest.social_published ?? 0}</div>
                  <div className="text-sm text-muted-foreground">Social Published</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <div className="text-3xl font-bold text-purple-600">
                    {latest.scenarios_with_ai && latest.scenarios_total
                      ? `${Math.round((latest.scenarios_with_ai / latest.scenarios_total) * 100)}%`
                      : '—'}
                  </div>
                  <div className="text-sm text-muted-foreground">Scenarios w/ AI</div>
                </div>
              </div>
              <div className="mt-6">
                <div className="text-sm font-medium mb-2">SEO Health Score: {latest.seo_health_score?.toFixed(1) ?? '—'}%</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip />
                    <Line type="monotone" dataKey="healthScore" stroke="#06b6d4" strokeWidth={2} name="SEO Health" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="queries">
          <Card>
            <CardHeader>
              <CardTitle>Top Search Queries</CardTitle>
              <CardDescription>What Google users are searching that brings up our pages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(latest.top_queries || []).map((q, i) => (
                  <div key={i} className="flex justify-between items-center p-2 border rounded text-sm">
                    <span className="font-medium">{q.query}</span>
                    <div className="flex gap-4 text-muted-foreground">
                      <span>Clicks: <strong className={q.clicks > 0 ? 'text-green-600' : 'text-red-600'}>{q.clicks}</strong></span>
                      <span>Impressions: <strong>{q.impressions}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="issues">
          <Card>
            <CardHeader>
              <CardTitle>Why Pages Aren't Indexed</CardTitle>
              <CardDescription>Reasons Google has not indexed pages from our site</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={latest.indexing_issues || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="reason" width={250} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="pages" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <CardTitle>Proposed Tasks</CardTitle>
              <CardDescription>Actions identified during today's audit</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(latest.proposed_tasks || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border rounded">
                    <Badge variant={priorityColors[t.priority] as any || 'outline'}>{t.priority}</Badge>
                    <span className="text-sm">{t.title}</span>
                  </div>
                ))}
              </div>
              {latest.news_topics && latest.news_topics.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium mb-2">Trending News Topics</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {latest.news_topics.map((n, i) => (
                      <li key={i}>• {n.topic}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SEODailyTracker;
